import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, TextField, Button, Divider, Stack,
  Accordion, AccordionSummary, AccordionDetails, CircularProgress,
} from '@mui/material';
import SearchableSelect from '../../../components/SearchableSelect';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { POSCliente } from './types';
import { formatCurrency, truncToDecimals } from '../../../utils/formatters';
import { isFechaVencida } from '../../../utils/facturaVentaFechas';
import type { ImpuestoPorTasaVenta } from '../../../utils/calcularTotalesIvaVenta';
import UbicacionSelector from '../../../components/UbicacionSelector';
import VendedorSelect from '../../../components/VendedorSelect';
import vendedorService, { Vendedor } from '../../../services/vendedorService';
import transporteService, { Transporte } from '../../../services/transporteService';
import listaPrecioService, { ListaPrecio } from '../../../services/listaPrecioService';
import direccionClienteService from '../../../services/direccionClienteService';
import { DireccionCliente } from '../../../interfaces/DireccionCliente';
import { esCondIvaMonotributo } from '../../../constants/condIvaCompania';
import companiaService from '../../../services/companiaService';
import { FORMAS_DE_PAGO } from './constants';
import NumeroInternoMeta from '../../../components/NumeroInternoMeta';

export interface POSSucursalOption {
  nroSuc: number;
  nombreSucursal?: string;
}

export interface POSCompaniaOption {
  idCompania: string;
  nombre?: string;
  condIva?: string;
}

interface Props {
  subtotalNeto: number;
  preciosConIva?: boolean;
  subtotalDisplay?: number;
  importeDesc1Display?: number;
  importeDesc2Display?: number;
  importeDesc3Display?: number;
  porcDesc: number;
  porcDesc2: number;
  porcDesc3: number;
  onPorcDescChange: (field: 'porcDesc' | 'porcDesc2' | 'porcDesc3', value: number) => void;
  /** P = porcentaje (default), M = monto fijo */
  tipoDesc?: string;
  tipoDesc2?: string;
  tipoDesc3?: string;
  importeDesc?: number;
  importeDesc2?: number;
  importeDesc3?: number;
  onTipoDescChange?: (field: 'tipoDesc' | 'tipoDesc2' | 'tipoDesc3', value: 'P' | 'M') => void;
  onImporteDescChange?: (field: 'importeDesc' | 'importeDesc2' | 'importeDesc3', value: number) => void;
  impuestos: ImpuestoPorTasaVenta[];
  montoIVA: number;
  total: number;
  observaciones: string;
  onObservacionesChange: (v: string) => void;
  cliente: POSCliente | null;
  /** Fecha de emisión en formato YYYY-MM-DD (input type="date") */
  fechaEmision: string;
  onFechaEmisionChange: (ymd: string) => void;
  /** Fecha de vencimiento comercial (YYYY-MM-DD); editable con mínimo = sugerida */
  fechaVencim?: string;
  fechaVencimMin?: string;
  fechaVencimHelperText?: string;
  onFechaVencimChange?: (ymd: string) => void;
  /** @deprecated ya no se edita manualmente */
  fechaVencimEditable?: boolean;
  /** Compañía emisora (catálogo cargado en el panel si no se pasa `companias`). */
  idCompania?: string;
  onIdCompaniaChange?: (idCompania: string) => void;
  companias?: POSCompaniaOption[];
  loadingCompanias?: boolean;
  nroVendedor: number;
  /** Si el nro está informado pero no viene en el catálogo, se muestra este nombre (p. ej. usuario legado). */
  nombreVendedorFallback?: string;
  onNroVendedorChange: (nroVendedor: number, nombreVendedor?: string) => void;
  /** Código de lista de precios vigente para el documento (p. ej. desde cliente o cabecera). */
  codigoLista: string;
  /** Al elegir vacío se limpia la lista explícita (precios según catálogo / sin lista). */
  onCodigoListaChange?: (codigoLista: string, descripcion?: string | null) => void;
  /** Solo pantallas que no usan FacturaPOSPagoPanel (pedido, cotización, NC). */
  formaDePago?: string;
  onFormaDePagoChange?: (v: string) => void;
  ordenCompra: string;
  onOrdenCompraChange: (v: string) => void;
  transporte: string;
  onTransporteChange: (v: string) => void;
  ubicacion: string;
  onUbicacionChange: (v: string) => void;
  /** Dirección de entrega (texto); opcional: solo se muestra si viene `onDireccionEnvioChange`. */
  direccionEnvio?: string;
  onDireccionEnvioChange?: (v: string) => void;
  /** Código de dirección de envío (tabla ClienteDireccion); con `onCodigoDireccionEnvioChange` se muestra el combo. */
  codigoDireccionEnvio?: string;
  onCodigoDireccionEnvioChange?: (
    codigo: string,
    detalle?: { direccion: string; codigoCiudad?: number | null },
  ) => void;
  saving: boolean;
  onGuardar: (emitir: boolean) => void;
  cantLineas: number;
  readOnly?: boolean;
  saveLabel?: string;
  /** Texto secundario bajo el botón principal (p. ej. fase AFIP / recibo). */
  saveSubLabel?: string;
  hideDraftButton?: boolean;
  /** Acciones extra debajo del botón principal (p. ej. conversión en pedidos). */
  footerActions?: React.ReactNode;
  /** Panel de cobro (FacturaPOSPagoPanel), entre Totales y Datos de emisión. */
  cobroPanel?: React.ReactNode;
  /** Control opcional del acordeón «Totales del documento» (p. ej. atajo F7). */
  totalesExpanded?: boolean;
  onTotalesExpandedChange?: (expanded: boolean) => void;
  sucursales?: POSSucursalOption[];
  nroSuc?: number;
  onNroSucChange?: (n: number) => void;
  loadingSucursales?: boolean;
  /** Receptor en cabecera (factura POS); si se pasan callbacks, se muestran en «Datos del cliente». */
  emisionNombre?: string;
  onEmisionNombreChange?: (v: string) => void;
  emisionCuit?: string;
  onEmisionCuitChange?: (v: string) => void;
  emisionTipoIva?: string;
  onEmisionTipoIvaChange?: (v: string) => void;
  /** Nro. de formulario interno (se muestra abajo a la derecha en alta, consulta y edición). */
  nroInterno?: number | null;
  /** Impide guardar/emitir (p. ej. cliente suspendido con bloqueo HARD). */
  guardadoBloqueado?: boolean;
  /** Bloquea compañía y sucursal/PV (modo rescate ARCA). */
  lockFiscalHeader?: boolean;
  /** `data-tour-id` opcional en el botón principal de emitir/guardar. */
  dataTourEmitId?: string;
  /** `data-tour-id` del bloque de totales/datos (sin el botón emitir). */
  dataTourPanelId?: string;
}

/** Mismas opciones y orden que `FacturaForm` en `/ventas/facturas/nuevo` (campo Tipo IVA). */
const TIPO_IVA_FACTURA_FORM_OPCIONES: { value: string; label: string }[] = [
  { value: 'I', label: 'Responsable Inscripto' },
  { value: 'C', label: 'Consumidor Final' },
  { value: 'M', label: 'Monotributo' },
  { value: 'E', label: 'Exento' },
  { value: 'N', label: 'No Categorizado' },
];
const TIPO_IVA_FORM_CODES = new Set(TIPO_IVA_FACTURA_FORM_OPCIONES.map((o) => o.value.toUpperCase()));

const normalizarCompaniaRow = (raw: unknown): POSCompaniaOption | null => {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const idCompania = String(o.idCompania ?? o.IdCompania ?? '').trim();
  if (!idCompania) return null;
  const nombre = String(o.nombre ?? o.Nombre ?? '').trim();
  const condIvaRaw = o.condIva ?? o.CondIva;
  const condIva = condIvaRaw != null ? String(condIvaRaw).trim() : undefined;
  return { idCompania, nombre: nombre || idCompania, condIva };
};

const normalizarListaPrecioRow = (x: ListaPrecio | Record<string, unknown>): ListaPrecio => {
  const o = x as Record<string, unknown>;
  const codigoLista = String(o.codigoLista ?? o.CodigoLista ?? '').trim();
  const descripcion = String(o.descripcion ?? o.Descripcion ?? '').trim();
  return { ...(x as ListaPrecio), codigoLista, descripcion };
};

const valorTransporteSelect = (t: Transporte): string =>
  String(t.nroExpreso ?? t.codigo ?? t.id ?? '').trim();

const etiquetaTransporteMenu = (t: Transporte): string => {
  const nombre = (t.nombreExpreso ?? t.nombre ?? '').trim();
  const nro = valorTransporteSelect(t);
  return nombre ? `${nombre} (${nro})` : `Expreso (${nro})`;
};

const normalizarDireccionClienteApi = (raw: unknown): DireccionCliente | null => {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const cod = String(r.codigoDireccion ?? r.CodigoDireccion ?? '').trim();
  if (!cod) return null;
  const nro = Number(r.nroCliente ?? r.NroCliente) || 0;
  const dir = String(r.direccion ?? r.Direccion ?? '').trim();
  const ccRaw = r.codigoCiudad ?? r.CodigoCiudad;
  const ccNum = ccRaw != null && ccRaw !== '' ? Number(ccRaw) : NaN;
  return {
    nroCliente: nro,
    codigoDireccion: cod,
    direccion: dir,
    codigoCiudad: Number.isFinite(ccNum) && ccNum > 0 ? ccNum : null,
  };
};

const etiquetaDireccionEnvioMenu = (d: DireccionCliente): string => {
  const cod = d.codigoDireccion;
  const dir = (d.direccion ?? '').trim();
  const max = 52;
  const short = dir.length > max ? `${dir.slice(0, max)}…` : dir;
  return short ? `${cod} — ${short}` : cod;
};

const TotalRow: React.FC<{
  label: string;
  value: number;
  bold?: boolean;
  large?: boolean;
  negative?: boolean;
}> = ({ label, value, bold, large, negative }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center" py={0.25}>
    <Typography
      variant={large ? 'body1' : 'body2'}
      fontWeight={bold ? 700 : 400}
      color={large ? 'text.primary' : 'text.secondary'}
    >
      {label}
    </Typography>
    <Typography
      variant={large ? 'body1' : 'body2'}
      fontWeight={bold ? 700 : 400}
      color={negative ? 'error.main' : large ? 'primary.main' : 'text.primary'}
    >
      {negative && value !== 0 ? `−${formatCurrency(value)}` : formatCurrency(value)}
    </Typography>
  </Stack>
);

const FacturaPOSTotalesPanel: React.FC<Props> = ({
  subtotalNeto,
  preciosConIva = false,
  subtotalDisplay,
  importeDesc1Display,
  importeDesc2Display,
  importeDesc3Display,
  porcDesc, porcDesc2, porcDesc3,
  onPorcDescChange,
  tipoDesc = 'P',
  tipoDesc2 = 'P',
  tipoDesc3 = 'P',
  importeDesc = 0,
  importeDesc2 = 0,
  importeDesc3 = 0,
  onTipoDescChange,
  onImporteDescChange,
  impuestos, montoIVA, total,
  observaciones, onObservacionesChange,
  cliente,
  fechaEmision, onFechaEmisionChange,
  fechaVencim, onFechaVencimChange,
  fechaVencimMin,
  fechaVencimHelperText,
  fechaVencimEditable: _fechaVencimEditable = false,
  idCompania = '',
  onIdCompaniaChange,
  companias: companiasProp,
  loadingCompanias: loadingCompaniasProp,
  nroVendedor,
  nombreVendedorFallback,
  onNroVendedorChange,
  codigoLista,
  onCodigoListaChange,
  formaDePago = '',
  onFormaDePagoChange,
  ordenCompra, onOrdenCompraChange,
  transporte, onTransporteChange,
  ubicacion, onUbicacionChange,
  direccionEnvio = '',
  onDireccionEnvioChange,
  codigoDireccionEnvio = '',
  onCodigoDireccionEnvioChange,
  saving, onGuardar, cantLineas,
  readOnly = false,
  saveLabel,
  saveSubLabel,
  hideDraftButton = false,
  footerActions,
  cobroPanel,
  totalesExpanded: totalesExpandedProp,
  onTotalesExpandedChange,
  sucursales,
  nroSuc = 0,
  onNroSucChange,
  loadingSucursales = false,
  emisionNombre,
  onEmisionNombreChange,
  emisionCuit,
  onEmisionCuitChange,
  emisionTipoIva,
  onEmisionTipoIvaChange,
  nroInterno,
  guardadoBloqueado = false,
  lockFiscalHeader = false,
  dataTourEmitId,
  dataTourPanelId,
}) => {
  const [showTotalesInternal, setShowTotalesInternal] = useState(false);
  const showTotales = totalesExpandedProp ?? showTotalesInternal;
  const setShowTotales = (next: boolean | ((prev: boolean) => boolean)) => {
    const resolved = typeof next === 'function' ? next(showTotales) : next;
    if (onTotalesExpandedChange) {
      onTotalesExpandedChange(resolved);
    } else {
      setShowTotalesInternal(resolved);
    }
  };
  const [showEmision, setShowEmision] = useState(false);
  const [showCliente, setShowCliente] = useState(false);
  const [showComercial, setShowComercial] = useState(false);
  const [showEntrega, setShowEntrega] = useState(false);
  const [vendedoresLista, setVendedoresLista] = useState<Vendedor[]>([]);
  const [loadingVendedores, setLoadingVendedores] = useState(true);
  const [transportesLista, setTransportesLista] = useState<Transporte[]>([]);
  const [loadingTransportes, setLoadingTransportes] = useState(true);
  const [listasPrecio, setListasPrecio] = useState<ListaPrecio[]>([]);
  const [loadingListasPrecio, setLoadingListasPrecio] = useState(true);
  const [direccionesEnvioLista, setDireccionesEnvioLista] = useState<DireccionCliente[]>([]);
  const [loadingDireccionesEnvio, setLoadingDireccionesEnvio] = useState(false);
  const [companiasInternas, setCompaniasInternas] = useState<POSCompaniaOption[]>([]);
  const [loadingCompaniasInternas, setLoadingCompaniasInternas] = useState(false);

  const catalogoCompaniasPropio = companiasProp === undefined;

  const companiasLista = useMemo(() => {
    const raw = catalogoCompaniasPropio ? companiasInternas : (companiasProp ?? []);
    return raw
      .map((c) => normalizarCompaniaRow(c) ?? null)
      .filter((c): c is POSCompaniaOption => c != null);
  }, [catalogoCompaniasPropio, companiasProp, companiasInternas]);
  const loadingCompanias = loadingCompaniasProp ?? loadingCompaniasInternas;
  const muestraCompania = onIdCompaniaChange != null;

  useEffect(() => {
    if (!muestraCompania || !catalogoCompaniasPropio) return;
    let cancelled = false;
    (async () => {
      setLoadingCompaniasInternas(true);
      try {
        const rawList = await companiaService.getCompanias({ pageNumber: 1, pageSize: 100 });
        const list = rawList
          .map((c: unknown) => normalizarCompaniaRow(c))
          .filter((c: POSCompaniaOption | null): c is POSCompaniaOption => c != null);
        if (!cancelled) setCompaniasInternas(list);
      } catch {
        if (!cancelled) setCompaniasInternas([]);
      } finally {
        if (!cancelled) setLoadingCompaniasInternas(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [muestraCompania, catalogoCompaniasPropio]);

  const idCompaniaNorm = String(idCompania ?? '').trim();

  const companiasOpciones = useMemo((): POSCompaniaOption[] => {
    const sorted = [...companiasLista];
    if (idCompaniaNorm && !sorted.some((c) => c.idCompania === idCompaniaNorm)) {
      return [{ idCompania: idCompaniaNorm, nombre: idCompaniaNorm }, ...sorted];
    }
    return sorted;
  }, [companiasLista, idCompaniaNorm]);

  const valorCompaniaSelect = useMemo(() => {
    if (companiasOpciones.length === 0) return '';
    if (idCompaniaNorm && companiasOpciones.some((c) => c.idCompania === idCompaniaNorm)) {
      return idCompaniaNorm;
    }
    // No forzar la primera: el padre hidrata preferida + compañía.
    return '';
  }, [idCompaniaNorm, companiasOpciones]);

  const esEmisorMonotributo = useMemo(() => {
    const id = valorCompaniaSelect || idCompaniaNorm;
    const condIva = companiasLista.find((c) => c.idCompania === id)?.condIva;
    return esCondIvaMonotributo(condIva);
  }, [companiasLista, valorCompaniaSelect, idCompaniaNorm]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingVendedores(true);
      try {
        const list = await vendedorService.getVendedores();
        if (!cancelled) setVendedoresLista(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setVendedoresLista([]);
      } finally {
        if (!cancelled) setLoadingVendedores(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingTransportes(true);
      try {
        const list = await transporteService.getTransportes();
        if (!cancelled) setTransportesLista(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setTransportesLista([]);
      } finally {
        if (!cancelled) setLoadingTransportes(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingListasPrecio(true);
      try {
        const res = await listaPrecioService.getListaPrecios({
          pageNumber: 1,
          pageSize: 2000,
        });
        const items = Array.isArray(res?.items) ? res.items : [];
        if (!cancelled) setListasPrecio(items);
      } catch {
        if (!cancelled) setListasPrecio([]);
      } finally {
        if (!cancelled) setLoadingListasPrecio(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (onCodigoDireccionEnvioChange == null || !cliente?.nroCliente) {
      setDireccionesEnvioLista([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingDireccionesEnvio(true);
      try {
        const list = await direccionClienteService.getDirecciones(cliente.nroCliente);
        const rows = (Array.isArray(list) ? list : [])
          .map(normalizarDireccionClienteApi)
          .filter((x): x is DireccionCliente => x != null);
        if (!cancelled) setDireccionesEnvioLista(rows);
      } catch {
        if (!cancelled) setDireccionesEnvioLista([]);
      } finally {
        if (!cancelled) setLoadingDireccionesEnvio(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cliente?.nroCliente, onCodigoDireccionEnvioChange]);

  const codigoDireccionEnvioTrim = String(codigoDireccionEnvio ?? '').trim();

  const direccionesEnvioOpciones = useMemo((): DireccionCliente[] => {
    const base = [...direccionesEnvioLista];
    if (
      codigoDireccionEnvioTrim
      && !base.some((d) => d.codigoDireccion === codigoDireccionEnvioTrim)
    ) {
      base.unshift({
        nroCliente: cliente?.nroCliente ?? 0,
        codigoDireccion: codigoDireccionEnvioTrim,
        direccion: String(direccionEnvio ?? '').trim() || codigoDireccionEnvioTrim,
        codigoCiudad: null,
      });
    }
    return base.sort((a, b) =>
      a.codigoDireccion.localeCompare(b.codigoDireccion, 'es', { sensitivity: 'base' }),
    );
  }, [direccionesEnvioLista, codigoDireccionEnvioTrim, direccionEnvio, cliente?.nroCliente]);

  const codigoDireccionEnvioSelectValue = useMemo(() => {
    if (!codigoDireccionEnvioTrim) return '';
    return direccionesEnvioOpciones.some((d) => d.codigoDireccion === codigoDireccionEnvioTrim)
      ? codigoDireccionEnvioTrim
      : '';
  }, [codigoDireccionEnvioTrim, direccionesEnvioOpciones]);

  const codigoListaActual = String(codigoLista ?? '').trim();

  const listasPrecioOpciones = useMemo((): ListaPrecio[] => {
    const sorted = [...listasPrecio]
      .map((x) => normalizarListaPrecioRow(x))
      .filter((l) => l.codigoLista)
      .sort((a, b) => {
        const la = `${a.descripcion || a.codigoLista}`.localeCompare(`${b.descripcion || b.codigoLista}`, 'es');
        if (la !== 0) return la;
        return a.codigoLista.localeCompare(b.codigoLista, 'es');
      });
    if (codigoListaActual && !sorted.some((l) => l.codigoLista === codigoListaActual)) {
      const descFb =
        (cliente?.listaPrecio?.codigoLista === codigoListaActual ? cliente?.listaPrecio?.descripcion : undefined)
        ?? codigoListaActual;
      return [
        {
          codigoLista: codigoListaActual,
          descripcion: String(descFb ?? '').trim() || codigoListaActual,
        } as ListaPrecio,
        ...sorted,
      ];
    }
    return sorted;
  }, [listasPrecio, codigoListaActual, cliente?.listaPrecio?.codigoLista, cliente?.listaPrecio?.descripcion]);

  const etiquetaListaMenu = (l: ListaPrecio): string => {
    const desc = (l.descripcion ?? '').trim();
    const cod = (l.codigoLista ?? '').trim();
    if (desc && cod) return `${desc} (${cod})`;
    return cod || desc || '—';
  };

  const transportesOpciones = useMemo((): Transporte[] => {
    const sorted = [...transportesLista].sort((a, b) =>
      etiquetaTransporteMenu(a).localeCompare(etiquetaTransporteMenu(b), 'es'),
    );
    const raw = (transporte ?? '').trim();
    if (raw && !sorted.some((t) => valorTransporteSelect(t) === raw)) {
      return [
        {
          nroExpreso: raw,
          nombreExpreso: `Expreso (${raw})`,
        } as Transporte,
        ...sorted,
      ];
    }
    return sorted;
  }, [transportesLista, transporte]);

  const tipoIvaEmisionNorm = String(emisionTipoIva ?? '').trim().toUpperCase();
  const tipoIvaOpciones = useMemo(() => {
    const base = [...TIPO_IVA_FACTURA_FORM_OPCIONES];
    if (tipoIvaEmisionNorm && !TIPO_IVA_FORM_CODES.has(tipoIvaEmisionNorm)) {
      return [{ value: tipoIvaEmisionNorm, label: `${tipoIvaEmisionNorm} (actual)` }, ...base];
    }
    return base;
  }, [tipoIvaEmisionNorm]);

  const subtotalMostrado = preciosConIva ? (subtotalDisplay ?? subtotalNeto + montoIVA) : subtotalNeto;
  const totalMostrar = truncToDecimals(total, 2);
  const resolveImporteTramo = (
    base: number,
    tipo: string,
    pct: number,
    importe: number,
    displayOverride?: number,
  ) => {
    if (preciosConIva && displayOverride != null) return displayOverride;
    if (String(tipo || 'P').toUpperCase() === 'M') {
      return Math.min(Math.max(0, base), Math.max(0, importe));
    }
    return base * (pct || 0) / 100;
  };
  const importeDesc1 = resolveImporteTramo(subtotalMostrado, tipoDesc, porcDesc, importeDesc, importeDesc1Display);
  const baseDesc2 = subtotalMostrado - importeDesc1;
  const importeDesc2Calc = resolveImporteTramo(baseDesc2, tipoDesc2, porcDesc2, importeDesc2, importeDesc2Display);
  const baseDesc3 = baseDesc2 - importeDesc2Calc;
  const importeDesc3Calc = resolveImporteTramo(baseDesc3, tipoDesc3, porcDesc3, importeDesc3, importeDesc3Display);
  const subtotalLabel = preciosConIva ? 'Subtotal Final' : 'Subtotal';

  const canSave = !!(cliente && cantLineas > 0) && !readOnly && !guardadoBloqueado;

  const discountFieldSx = {
    '& .MuiInputBase-input': { textAlign: 'right', fontSize: '0.82rem', padding: '6px 8px' },
  };

  const descTramos = [
    {
      key: 1,
      tipo: String(tipoDesc || 'P').toUpperCase() === 'M' ? 'M' : 'P',
      pct: porcDesc,
      importe: importeDesc,
      onTipo: (v: 'P' | 'M') => onTipoDescChange?.('tipoDesc', v),
      onPct: (v: number) => onPorcDescChange('porcDesc', v),
      onImporte: (v: number) => onImporteDescChange?.('importeDesc', v),
      labelPct: 'Desc 1%',
      montoMostrado: importeDesc1,
    },
    {
      key: 2,
      tipo: String(tipoDesc2 || 'P').toUpperCase() === 'M' ? 'M' : 'P',
      pct: porcDesc2,
      importe: importeDesc2,
      onTipo: (v: 'P' | 'M') => onTipoDescChange?.('tipoDesc2', v),
      onPct: (v: number) => onPorcDescChange('porcDesc2', v),
      onImporte: (v: number) => onImporteDescChange?.('importeDesc2', v),
      labelPct: 'Desc 2%',
      montoMostrado: importeDesc2Calc,
    },
    {
      key: 3,
      tipo: String(tipoDesc3 || 'P').toUpperCase() === 'M' ? 'M' : 'P',
      pct: porcDesc3,
      importe: importeDesc3,
      onTipo: (v: 'P' | 'M') => onTipoDescChange?.('tipoDesc3', v),
      onPct: (v: number) => onPorcDescChange('porcDesc3', v),
      onImporte: (v: number) => onImporteDescChange?.('importeDesc3', v),
      labelPct: 'Desc 3%',
      montoMostrado: importeDesc3Calc,
    },
  ] as const;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box
        {...(dataTourPanelId ? { 'data-tour-id': dataTourPanelId } : {})}
        sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
      >
      {/* Totales del documento (colapsable, contraído al iniciar) */}
      <Accordion
        id="pos-totales-documento"
        expanded={showTotales}
        onChange={(_, expanded) => setShowTotales(expanded)}
        variant="outlined"
        disableGutters
        sx={{ borderRadius: '8px !important', '&:before': { display: 'none' } }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            minHeight: 'unset',
            py: 0.75,
            '& .MuiAccordionSummary-content': { my: 0.25, alignItems: 'flex-start' },
          }}
        >
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={1}
            sx={{ width: '100%', pr: 0.5, minWidth: 0, flex: 1 }}
          >
            <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ minWidth: 0 }}>
              <CalculateOutlinedIcon fontSize="small" color="action" sx={{ flexShrink: 0, mt: 0.15 }} />
              <Typography variant="body2" lineHeight={1.2} component="div">
                Totales del
                <br />
                documento
              </Typography>
            </Stack>
            {totalMostrar > 0 && (
              <Stack spacing={0.125} alignItems="flex-end" sx={{ flexShrink: 0, textAlign: 'right' }}>
                {!preciosConIva && montoIVA > 0 && (
                  <Typography variant="caption" color="text.secondary" lineHeight={1.2}>
                    IVA {formatCurrency(montoIVA)}
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  fontWeight={700}
                  color="primary.main"
                  lineHeight={1.25}
                >
                  {formatCurrency(totalMostrar)}
                </Typography>
              </Stack>
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
          <TotalRow label={subtotalLabel} value={subtotalMostrado} />
          {preciosConIva && !esEmisorMonotributo && subtotalMostrado > 0 && (
            <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
              IVA incluido en precios
            </Typography>
          )}

          <Box mt={1} mb={0.5}>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.75}>
              Descuentos globales
            </Typography>
            <Stack spacing={1}>
              {descTramos.map((tramo, i) => (
                <Stack key={tramo.key} direction="row" spacing={0.75} alignItems="center">
                  <Button
                    size="small"
                    variant={tramo.tipo === 'P' ? 'contained' : 'outlined'}
                    onClick={() => tramo.onTipo('P')}
                    disabled={readOnly}
                    sx={{ minWidth: 36, px: 0.5, py: 0.25, fontSize: '0.7rem' }}
                  >
                    %
                  </Button>
                  <Button
                    size="small"
                    variant={tramo.tipo === 'M' ? 'contained' : 'outlined'}
                    onClick={() => tramo.onTipo('M')}
                    disabled={readOnly}
                    sx={{ minWidth: 36, px: 0.5, py: 0.25, fontSize: '0.7rem' }}
                  >
                    $
                  </Button>
                  <TextField
                    label={tramo.tipo === 'M' ? `Desc ${i + 1} $` : tramo.labelPct}
                    size="small"
                    type="number"
                    value={tramo.tipo === 'M' ? (tramo.importe || '') : (tramo.pct || '')}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value) || 0;
                      if (tramo.tipo === 'M') tramo.onImporte(v);
                      else tramo.onPct(v);
                    }}
                    inputProps={{
                      min: 0,
                      max: tramo.tipo === 'P' ? 100 : undefined,
                      id: `pos-bonif-${i + 1}`,
                    }}
                    disabled={readOnly}
                    sx={{ flex: 1, ...discountFieldSx }}
                  />
                </Stack>
              ))}
            </Stack>
            {(importeDesc1 > 0 || importeDesc2Calc > 0 || importeDesc3Calc > 0) && (
              <Box mt={0.75}>
                {importeDesc1 > 0 && (
                  <TotalRow
                    label={descTramos[0].tipo === 'M' ? 'Desc 1 ($)' : `Desc 1 (${porcDesc}%)`}
                    value={importeDesc1}
                    negative
                  />
                )}
                {importeDesc2Calc > 0 && (
                  <TotalRow
                    label={descTramos[1].tipo === 'M' ? 'Desc 2 ($)' : `Desc 2 (${porcDesc2}%)`}
                    value={importeDesc2Calc}
                    negative
                  />
                )}
                {importeDesc3Calc > 0 && (
                  <TotalRow
                    label={descTramos[2].tipo === 'M' ? 'Desc 3 ($)' : `Desc 3 (${porcDesc3}%)`}
                    value={importeDesc3Calc}
                    negative
                  />
                )}
              </Box>
            )}
          </Box>

          {!preciosConIva && impuestos.length > 0 ? (
            <Box
              sx={{
                mt: 1,
                p: 1,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'info.light',
                bgcolor: 'rgba(33, 150, 243, 0.04)',
              }}
            >
              <Typography variant="caption" color="info.main" fontWeight={700} display="block" mb={0.5}>
                Impuestos
              </Typography>
              {impuestos.map((imp) => (
                <TotalRow
                  key={`iva-${imp.tasa}`}
                  label={`IVA ${imp.tasa % 1 === 0 ? imp.tasa.toFixed(0) : imp.tasa.toFixed(1)}%`}
                  value={imp.monto}
                />
              ))}
            </Box>
          ) : !preciosConIva && montoIVA === 0 && subtotalNeto > 0 ? (
            <TotalRow label="IVA" value={0} />
          ) : null}
          <Divider sx={{ my: 1 }} />
          <TotalRow label="TOTAL" value={totalMostrar} bold large />
          <TextField
            label="Observaciones"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={observaciones}
            onChange={(e) => onObservacionesChange(e.target.value)}
            disabled={readOnly}
            sx={{ mt: 1.5 }}
          />
        </AccordionDetails>
      </Accordion>

      {cobroPanel}

      {/* Datos de emisión (colapsable) */}
      <Accordion
        expanded={showEmision}
        onChange={() => setShowEmision((p) => !p)}
        variant="outlined"
        disableGutters
        sx={{ borderRadius: '8px !important', '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { my: 0 } }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CalendarTodayIcon fontSize="small" color="action" />
            <Typography variant="body2">Datos de emisión</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <Stack spacing={1.5}>
            <TextField
              label="Fecha de emisión"
              type="date"
              size="small"
              fullWidth
              value={fechaEmision}
              onChange={(e) => onFechaEmisionChange(e.target.value)}
              disabled={readOnly}
              InputLabelProps={{ shrink: true }}
            />
            {fechaVencim != null && (
              <TextField
                label="Fecha de vencimiento"
                type="date"
                size="small"
                fullWidth
                value={fechaVencim}
                onChange={
                  onFechaVencimChange && !readOnly
                    ? (e) => onFechaVencimChange(e.target.value)
                    : undefined
                }
                disabled={readOnly || !onFechaVencimChange}
                InputLabelProps={{ shrink: true }}
                inputProps={fechaVencimMin ? { min: fechaVencimMin } : undefined}
                error={
                  (fechaVencimMin != null && fechaVencim < fechaVencimMin)
                  || (readOnly && isFechaVencida(fechaVencim))
                }
                helperText={
                  fechaVencimMin != null && fechaVencim < fechaVencimMin
                    ? 'No puede ser anterior a la sugerida'
                    : readOnly && isFechaVencida(fechaVencim)
                      ? 'Vencida'
                      : fechaVencimHelperText
                }
              />
            )}
            {muestraCompania && (
              loadingCompanias ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                  <CircularProgress size={22} />
                </Box>
              ) : companiasOpciones.length > 0 ? (
                <SearchableSelect
                  label="Compañía"
                  size="small"
                  disabled={readOnly || lockFiscalHeader}
                  value={valorCompaniaSelect || null}
                  onChange={(v) => onIdCompaniaChange!(v ?? '')}
                  options={companiasOpciones.map((c) => ({
                    value: c.idCompania,
                    label: c.nombre && c.nombre !== c.idCompania
                      ? `${c.idCompania} — ${c.nombre}`
                      : c.idCompania,
                  }))}
                />
              ) : null
            )}
            {onNroSucChange != null && (
              loadingSucursales ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                  <CircularProgress size={22} />
                </Box>
              ) : sucursales != null && sucursales.length > 0 ? (
                <SearchableSelect<number>
                  label="Sucursal / Punto de Venta"
                  size="small"
                  disabled={readOnly || lockFiscalHeader}
                  value={nroSuc || null}
                  onChange={(v) => onNroSucChange(v ?? 0)}
                  options={sucursales.map((s) => ({
                    value: s.nroSuc,
                    label: s.nombreSucursal ? `${s.nroSuc} — ${s.nombreSucursal}` : `Suc. ${s.nroSuc}`,
                  }))}
                />
              ) : null
            )}
            {onFormaDePagoChange != null && (
              <SearchableSelect
                label="Forma de pago"
                size="small"
                disabled={readOnly}
                allowEmpty
                emptyOptionLabel="Seleccionar"
                value={formaDePago || null}
                onChange={(v) => onFormaDePagoChange(v ?? '')}
                options={FORMAS_DE_PAGO.map((f) => ({ value: f, label: f }))}
              />
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Datos del cliente (colapsable): receptor y vendedor */}
      <Accordion
        expanded={showCliente}
        onChange={() => setShowCliente((p) => !p)}
        variant="outlined"
        disableGutters
        sx={{ borderRadius: '8px !important', '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { my: 0 } }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <PersonOutlineIcon fontSize="small" color="action" />
            <Typography variant="body2">Datos del cliente</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <Stack spacing={1.5}>
            {onEmisionNombreChange != null
              && onEmisionCuitChange != null
              && onEmisionTipoIvaChange != null && (
              <>
                <TextField
                  label="Nombre"
                  size="small"
                  fullWidth
                  value={emisionNombre ?? ''}
                  onChange={(e) => onEmisionNombreChange!(e.target.value)}
                  disabled={readOnly || !cliente}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 120 }}
                />
                <TextField
                  label={
                    tipoIvaEmisionNorm === 'C' || tipoIvaEmisionNorm === 'CF'
                      ? 'DNI / CUIT'
                      : 'CUIT'
                  }
                  size="small"
                  fullWidth
                  value={emisionCuit ?? ''}
                  onChange={(e) => onEmisionCuitChange!(e.target.value)}
                  disabled={readOnly || !cliente}
                  InputLabelProps={{ shrink: true }}
                />
                <SearchableSelect
                  label="Tipo IVA"
                  size="small"
                  disabled={readOnly || !cliente}
                  allowEmpty
                  emptyOptionLabel="Seleccione..."
                  value={tipoIvaEmisionNorm || null}
                  onChange={(v) => {
                    const raw = String(v ?? '').trim();
                    onEmisionTipoIvaChange!(raw ? raw.toUpperCase() : '');
                  }}
                  options={tipoIvaOpciones.map((o) => ({ value: o.value, label: o.label }))}
                />
              </>
            )}
            <VendedorSelect
              value={nroVendedor > 0 ? nroVendedor : null}
              onChange={(nro, vendedor) =>
                onNroVendedorChange(nro ?? 0, vendedor?.nombreVendedor ?? undefined)
              }
              vendedores={vendedoresLista}
              loading={loadingVendedores}
              allowEmpty
              emptyOptionLabel="Seleccionar"
              nombreVendedorFallback={nombreVendedorFallback}
              size="small"
              disabled={readOnly}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Condiciones comerciales (colapsable, mismo patrón que Entrega / Logística) */}
      <Accordion
        expanded={showComercial}
        onChange={() => setShowComercial((p) => !p)}
        variant="outlined"
        disableGutters
        sx={{ borderRadius: '8px !important', '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { my: 0 } }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AssignmentIndIcon fontSize="small" color="action" />
            <Typography variant="body2">Lista y referencias</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <Stack spacing={1.5}>
            {onCodigoListaChange != null && (
              loadingListasPrecio ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                  <CircularProgress size={22} />
                </Box>
              ) : (
                <SearchableSelect
                  label="Lista de precios"
                  size="small"
                  disabled={readOnly || !cliente}
                  allowEmpty
                  emptyOptionLabel="Seleccionar"
                  value={codigoListaActual || null}
                  onChange={(raw) => {
                    if (!raw) {
                      onCodigoListaChange('');
                      return;
                    }
                    const found = listasPrecioOpciones.find((l) => l.codigoLista === raw);
                    onCodigoListaChange(raw, found?.descripcion ?? null);
                  }}
                  options={listasPrecioOpciones.map((l) => ({
                    value: l.codigoLista,
                    label: etiquetaListaMenu(l),
                  }))}
                />
              )
            )}
            <TextField
              label="Orden de compra"
              size="small"
              fullWidth
              value={ordenCompra}
              onChange={(e) => onOrdenCompraChange(e.target.value)}
              disabled={readOnly}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Entrega colapsable */}
      <Accordion
        expanded={showEntrega}
        onChange={() => setShowEntrega((p) => !p)}
        variant="outlined"
        disableGutters
        sx={{ borderRadius: '8px !important', '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { my: 0 } }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <LocalShippingIcon fontSize="small" color="action" />
            <Typography variant="body2">Entrega / Logística</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <Stack spacing={1.5}>
            {loadingTransportes ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <CircularProgress size={22} />
              </Box>
            ) : (
              <SearchableSelect
                label="Transporte / Expreso"
                size="small"
                disabled={readOnly}
                allowEmpty
                emptyOptionLabel="Seleccionar"
                value={transporte || null}
                onChange={(v) => onTransporteChange(v ?? '')}
                options={transportesOpciones.map((t) => {
                  const v = valorTransporteSelect(t);
                  return { value: v, label: etiquetaTransporteMenu(t) };
                })}
              />
            )}
            {onCodigoDireccionEnvioChange != null && (
              <>
                {loadingDireccionesEnvio ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                    <CircularProgress size={22} />
                  </Box>
                ) : (
                  <SearchableSelect
                    label="Código de dirección de envío"
                    size="small"
                    disabled={readOnly || !cliente}
                    allowEmpty
                    emptyOptionLabel={
                      direccionesEnvioOpciones.length ? 'Sin selección' : 'Sin direcciones cargadas'
                    }
                    value={codigoDireccionEnvioSelectValue || null}
                    onChange={(raw) => {
                      if (!raw) {
                        onCodigoDireccionEnvioChange('');
                        return;
                      }
                      const row = direccionesEnvioOpciones.find((d) => d.codigoDireccion === raw);
                      onCodigoDireccionEnvioChange(raw, {
                        direccion: String(row?.direccion ?? '').trim(),
                        codigoCiudad: row?.codigoCiudad,
                      });
                    }}
                    options={direccionesEnvioOpciones.map((d) => ({
                      value: d.codigoDireccion,
                      label: etiquetaDireccionEnvioMenu(d),
                    }))}
                  />
                )}
              </>
            )}
            {onDireccionEnvioChange != null && (
              <TextField
                label="Dirección de envío"
                size="small"
                fullWidth
                multiline
                minRows={2}
                value={direccionEnvio}
                onChange={(e) => onDireccionEnvioChange(e.target.value)}
                disabled={readOnly || !cliente}
                InputLabelProps={{ shrink: true }}
                placeholder={cliente ? 'Texto de entrega (se completa al elegir código; editable)' : ''}
              />
            )}
            <UbicacionSelector
              value={ubicacion}
              onChange={(v) => onUbicacionChange(v)}
              disabled={readOnly}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>
      </Box>

      {!readOnly && (
        <Stack
          spacing={1}
          {...(dataTourEmitId ? { 'data-tour-id': dataTourEmitId } : {})}
        >
          {!hideDraftButton && (
            <Button
              variant="outlined"
              fullWidth
              startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={() => onGuardar(false)}
              disabled={saving || !canSave}
              sx={{ textTransform: 'none' }}
            >
              Guardar borrador
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            fullWidth
            startIcon={saving ? <CircularProgress size={16} /> : <ReceiptIcon />}
            onClick={() => onGuardar(true)}
            disabled={saving || !canSave}
            sx={{ py: 1.2, fontWeight: 600, textTransform: 'none' }}
          >
            {saveLabel ?? 'Emitir factura'}
          </Button>
          {saveSubLabel && (
            <Typography variant="caption" color="text.secondary" textAlign="center" display="block">
              {saveSubLabel}
            </Typography>
          )}
          {footerActions}
        </Stack>
      )}

      <NumeroInternoMeta nro={nroInterno} />
    </Box>
  );
};

export default FacturaPOSTotalesPanel;
